import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  AcceptRegistrationItemDocument,
  GetOpenRegistrationCountDocument,
  GetRegistrationItemsDocument,
  RegistrationDetailItemDataFragment,
  RegistrationPersonUpdateInput,
  RejectRegistrationItemDocument,
  UpdateRegistrationPersonDocument,
} from '../../types/api/graphql';

/** Both transitions change the list and the number in the menu. */
const refetchQueries = [
  GetRegistrationItemsDocument,
  GetOpenRegistrationCountDocument,
];

/**
 * Accept or reject one registration.
 *
 * Both transitions are final and the server refuses a second attempt on a
 * request that was already handled, so callers must surface the thrown error
 * rather than swallow it. Both refresh the list and the menu badge; the
 * detail itself updates through the returned registration, which the cache
 * normalises onto the same `Registration` it already holds.
 */
export function useRegistrationHandling() {
  const [acceptRegistrationItem, { loading: acceptRegistrationItemLoading }] =
    useMutation(AcceptRegistrationItemDocument, { refetchQueries });

  const [rejectRegistrationItem, { loading: rejectRegistrationItemLoading }] =
    useMutation(RejectRegistrationItemDocument, { refetchQueries });

  const [
    updateRegistrationPerson,
    { loading: updateRegistrationPersonLoading },
  ] = useMutation(UpdateRegistrationPersonDocument);

  const accept = useCallback(
    async (
      registrationId: string
    ): Promise<RegistrationDetailItemDataFragment | undefined> => {
      const result = await acceptRegistrationItem({
        variables: { registrationId },
      });

      return (
        (result.data?.registrationAccept
          .registration as RegistrationDetailItemDataFragment) ?? undefined
      );
    },
    [acceptRegistrationItem]
  );

  const reject = useCallback(
    async (
      registrationId: string,
      reason: string
    ): Promise<RegistrationDetailItemDataFragment | undefined> => {
      const result = await rejectRegistrationItem({
        variables: { registrationId, reason },
      });

      return (
        (result.data?.registrationReject
          .registration as RegistrationDetailItemDataFragment) ?? undefined
      );
    },
    [rejectRegistrationItem]
  );

  /**
   * Correct the data of one person before accepting, so a typo or a taken
   * address does not force the household to register again. Refused by the
   * server when a field breaks the form's rules or the address is already in
   * use; callers surface that error.
   */
  const updatePerson = useCallback(
    async (
      input: RegistrationPersonUpdateInput
    ): Promise<RegistrationDetailItemDataFragment | undefined> => {
      const result = await updateRegistrationPerson({ variables: { input } });

      return (
        (result.data?.registrationPersonUpdate
          .registration as RegistrationDetailItemDataFragment) ?? undefined
      );
    },
    [updateRegistrationPerson]
  );

  return {
    accept,
    reject,
    updatePerson,
    loading:
      acceptRegistrationItemLoading ||
      rejectRegistrationItemLoading ||
      updateRegistrationPersonLoading,
  };
}
